<script setup lang="ts">
import { useJobStore } from '@/stores/jobs.js';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

const { formattedDate, resetJobs, loadJobs } = useJobStore();
const {
  showCurrentQueue,
  currentQueue,
  currentStatus,
  currentOffset,
  currentPageSize,
  aggregates,
  jobs,
  aggregateJobTotal,
  aggregateJobSorted,
  aggregateShopSorted,
  aggregateShopTotal,
  isLoading,
} = storeToRefs(useJobStore());

const expandedJob = ref(null);

function updateExpandedJob(job) {
  if (expandedJob.value && expandedJob.value.id === job.id) {
    expandedJob.value = null;
  } else {
    expandedJob.value = job;
  }
}

function updateOffset(event) {
  const offset = parseInt(event.target.value);
  if (!isNaN(offset) && offset != currentOffset) {
    loadJobs(currentQueue.value, currentStatus.value, 100, offset);
  }
}

function next() {
  const offset = currentOffset.value + currentPageSize.value;
  loadJobs(currentQueue.value, currentStatus.value, 100, offset);
}

function previous() {
  const offset = Math.max(currentOffset.value - currentPageSize.value, 0);
  loadJobs(currentQueue.value, currentStatus.value, 100, offset);
}

function refresh() {
  loadJobs(currentQueue.value, currentStatus.value, 100, currentOffset.value);
}

function hide() {
  resetJobs();
  showCurrentQueue.value = false;
}
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between mb-8">
      <div class="flex items-center space-x-2">
        <button @click="hide()">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h2 class="capitalize">{{ currentQueue }}</h2>
        <h2>/</h2>
        <h2 class="capitalize">{{ currentStatus }}</h2>
      </div>
      <div class="flex items-center">
        <div class="inline-flex rounded-lg shadow-sm">
          <button
            type="button"
            :disabled="currentOffset === 0"
            @click="previous"
            class="py-2 px-3 inline-flex justify-center items-center gap-2 -ms-px first:rounded-s-lg first:ms-0 last:rounded-e-lg text-sm font-medium focus:z-10 border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            Prev
          </button>
          <input
            type="text"
            pattern="[0-9]*"
            title="offset"
            placeholder="offset"
            :value="currentOffset"
            @change="updateOffset"
            class="py-2 px-3 max-w-20 border border-gray-200 bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-1 focus:z-10 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            :disabled="jobs.length < currentPageSize"
            @click="next"
            class="py-2 px-3 inline-flex justify-center items-center gap-2 -ms-px first:rounded-s-lg first:ms-0 last:rounded-e-lg text-sm font-medium focus:z-10 border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
          >
            Next
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
        <button
          class="ml-2 text-gray-500 cursor-pointer disabled:text-gray-200 disabled:animate-spin"
          :disabled="isLoading"
          @click="refresh"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
        </button>
      </div>
    </div>
    <div>
      <div class="grid grid-cols-1 sm:grid-cols-2 space-x-4">
        <div class="flex flex-col bg-white border shadow-sm rounded-xl">
          <div class="max-h-64 p-4 sm:p-8 space-y-5 overflow-auto">
            <div class="flex justify-between items-center">
              <p class="text-lg uppercase tracking-wide text-gray-500">Job Breakdown</p>
              <p class="text-xl font-medium text-gray-800">{{ Object.keys(aggregateJobSorted).length }}</p>
            </div>
            <div class="animate-pulse" v-if="isLoading">
              <div class="h-2 bg-slate-200 rounded w-full"></div>
            </div>
            <template v-else v-for="(count, job) in aggregateJobSorted">
              <div>
                <div class="mb-2 flex justify-between items-center">
                  <h3 class="text-sm font-semibold text-gray-800 truncate" :title="job">{{ job }}</h3>
                  <span class="text-sm text-gray-800">{{ count }}</span>
                </div>
                <div
                  class="flex w-full h-2 bg-gray-200 rounded-full overflow-hidden"
                  role="progressbar"
                  aria-valuenow="25"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <div
                    class="flex flex-col justify-center rounded-full overflow-hidden bg-blue-600 text-xs text-white text-center whitespace-nowrap transition duration-500"
                    :style="{ width: `${(count / aggregateJobTotal) * 100}%` }"
                  ></div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="flex flex-col bg-white border shadow-sm rounded-xl">
          <div class="max-h-64 p-4 sm:p-8 space-y-5 overflow-auto">
            <div class="flex justify-between items-center">
              <p class="text-lg uppercase tracking-wide text-gray-500">Shop Breakdown</p>
              <p class="text-xl font-medium text-gray-800">{{ Object.keys(aggregateShopSorted).length }}</p>
            </div>
            <div class="animate-pulse" v-if="isLoading">
              <div class="h-2 bg-slate-200 rounded w-full"></div>
            </div>
            <template v-else v-for="(count, shop) in aggregateShopSorted">
              <div>
                <div class="mb-2 flex justify-between items-center">
                  <h3 class="text-sm font-semibold text-gray-800">{{ shop }}</h3>
                  <span class="text-sm text-gray-800">{{ count }}</span>
                </div>
                <div
                  class="flex w-full h-2 bg-gray-200 rounded-full overflow-hidden"
                  role="progressbar"
                  aria-valuenow="25"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <div
                    class="flex flex-col justify-center rounded-full overflow-hidden bg-blue-600 text-xs text-white text-center whitespace-nowrap transition duration-500"
                    :style="{ width: `${(count / aggregateShopTotal) * 100}%` }"
                  ></div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col bg-white border shadow-sm rounded-xl">
      <div class="p-4 sm:p-8">
        <div class="mb-5 flex justify-between items-center">
          <p class="text-lg uppercase tracking-wide text-gray-500">Jobs</p>
          <p class="text-xl font-medium text-gray-800">{{ Object.keys(jobs).length }}</p>
        </div>
        <div class="animate-pulse" v-if="isLoading">
          <div class="p-4 border-b flex items-center justify-between animate-pulse">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full">
              <div class="h-2 bg-slate-200 rounded col-span-2"></div>
              <div class="h-2 bg-slate-200 rounded col-span-1"></div>
            </div>
          </div>
        </div>
        <div v-else>
          <template v-for="job in jobs">
            <div>
              <div class="px-4 cursor-pointer" @click="updateExpandedJob(job)">
                <div class="p-4 border-b flex items-center justify-between">
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-2" v-if="currentStatus == 'pending'">
                    <div class="truncate col-span-2" :title="job.job">{{ job.job }}</div>
                    <div class="text-neutral-500 truncate">{{ job.data.shop }}</div>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-2" v-else>
                    <div class="truncate">{{ job.job }}</div>
                    <div class="text-neutral-500 truncate">{{ job.data.shop }}</div>
                    <div class="text-neutral-500 text-xs truncate">
                      {{ formattedDate(job.timestamp) }}
                    </div>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="m4.5 15.75 7.5-7.5 7.5 7.5"
                      v-if="expandedJob && expandedJob.id == job.id"
                    />
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" v-else />
                  </svg>
                </div>
              </div>
              <div v-if="expandedJob && expandedJob.id == job.id" class="p-6 bg-slate-50">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div class="space-y-3">
                    <dl class="flex flex-col sm:flex-row gap-1">
                      <dt class="min-w-20">
                        <span class="block text-sm text-gray-500">Shop:</span>
                      </dt>
                      <dd>
                        <ul>
                          <li class="text-sm text-gray-800">
                            {{ expandedJob.data.shop }}
                          </li>
                        </ul>
                      </dd>
                    </dl>
                    <dl class="flex flex-col sm:flex-row gap-1" v-if="currentStatus !== 'pending'">
                      <dt class="min-w-20">
                        <span class="block text-sm text-gray-500" v-if="currentStatus === 'reserved'">Reserved:</span>
                        <span class="block text-sm text-gray-500" v-else>Delayed Until:</span>
                      </dt>
                      <dd>
                        <ul>
                          <li
                            class="text-sm text-gray-800"
                            :title="expandedJob.humanTimestamp"
                            v-if="currentStatus === 'reserved'"
                          >
                            {{ formattedDate(expandedJob.timestamp - 999999) }}
                          </li>
                          <li class="text-sm text-gray-800" :title="expandedJob.humanTimestamp" v-else>
                            {{ formattedDate(expandedJob.timestamp) }}
                          </li>
                        </ul>
                      </dd>
                    </dl>
                  </div>
                  <div
                    class="sm:col-span-2 bg-neutral-800 p-4 rounded-xl text-green-500 overflow-auto font-mono text-xs"
                  >
                    <pre>{{ expandedJob.data }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
